<template>
  <section v-if="loading || hasData">
    <page-sub-header
      :title="bankingInfo.stream.label"
      :subtitle="isDataReady ? timeSeriesSubtitle : undefined"
      class="mt-4"
    >
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`stream-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else>
      <chart-with-companion-kpis
        v-if="timeGroupedStreamersDataset.values.length"
        card-class="mb-4"
        chart-title="Number of Pay users over time"
        chart-subtitle="Number of employees"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedStreamersDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedStreamersDataset.name } } },
            ...createCountWithPercentageTooltip(timeGroupedStreamerPercentageDataset),
          }"
          fill-gradient
        />
        <template #companion>
          <div class="stats-combined mb-3">
            <app-stat
              :stat="lastGroupedStreamers"
              :previous-stat="prevGroupedStreamers"
              :formatter="toInteger"
              description="Pay users"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
              delta-neutral
            />
            <app-stat
              :stat="lastGroupedStreamersPercentage"
              :previous-stat="prevGroupedStreamersPercentage"
              :formatter="toNumber"
              suffix="%"
              description="Pay Users Percentage"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
              delta-neutral
            />
          </div>
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastGroupedStreamers) }} employees ({{
              lastGroupedStreamersPercentage
            }}%) {{ portalUpdatesJan2026Enabled ? 'accessed flexible pay' : 'transferred' }}.
            {{ streamersPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <b-row class="mb-4">
        <b-col lg="4" class="mb-4 mb-lg-0">
          <sme-card v-pdf-chart-group class="h-100 overflow-hidden">
            <app-stat
              :stat="averageAmountDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              :description="
                portalUpdatesJan2026Enabled
                  ? `Average amount per ${bankingInfo.stream.substantive.toLowerCase()}`
                  : `Average ${bankingInfo.stream.substantive.toLowerCase()} value`
              "
              :class="[averageAmountDataset.values.length && 'mb-3']"
              background-color="core-5-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="averageAmountDataset.values.length" title="Average transfer value" layout="triple">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="averageAmountDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col lg="4" class="mb-4 mb-lg-0">
          <sme-card v-pdf-chart-group class="h-100 overflow-hidden">
            <app-stat
              :stat="transfersDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :description="`Average ${timeSeriesUnit}ly ${bankingInfo.stream.substantive.toLowerCase()}s`"
              :class="[transfersDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="transfersDataset.values.length" title="Average transfers" layout="triple">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="transfersDataset"
                :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col lg="4" class="mb-lg-0">
          <sme-card v-pdf-chart-group class="h-100 overflow-hidden">
            <app-stat
              :stat="totalAmountDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              :description="
                portalUpdatesJan2026Enabled
                  ? `Average ${timeSeriesUnit}ly total accessed`
                  : `Average ${timeSeriesUnit}ly ${bankingInfo.stream.substantive.toLowerCase()} value total`
              "
              :class="[totalAmountDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart v-if="totalAmountDataset.values.length" title="Average total transfer value" layout="triple">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="totalAmountDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>

      <sme-card v-if="streamReasonsDatasets?.some(dataset => !!dataset.values.length)" v-pdf-chart-group>
        <template v-if="portalUpdatesJan2026Enabled">
          <h6 class="mb-3 transfer-reasons-title">Reason for Transfer</h6>
          <b-row class="align-items-center">
            <b-col md="6" class="d-flex justify-content-center mb-3 mb-md-0">
              <div class="transfer-reasons-chart">
                <pdf-chart title="Reason for Transfer">
                  <donut-chart
                    :labels="transferReasonsLabels"
                    :values="transferReasonsValues"
                    :colors="transferReasonsColors"
                    :show-data-labels="false"
                    cutout="0%"
                    :chart-options="{
                      aspectRatio: 1,
                      plugins: { tooltip: { enabled: true }, legend: { display: false } },
                    }"
                  />
                </pdf-chart>
              </div>
            </b-col>
            <b-col md="6">
              <div class="transfer-reasons-legend">
                <div v-for="(label, index) in transferReasonsLabels" :key="label" class="transfer-reasons-legend__item">
                  <span
                    class="transfer-reasons-legend__dot"
                    :style="{ backgroundColor: transferReasonsColors[index] }"
                  />
                  <span class="transfer-reasons-legend__label">{{ label }}</span>
                  <span class="transfer-reasons-legend__value">{{ transferReasonsValues[index] }}%</span>
                </div>
              </div>
            </b-col>
          </b-row>
        </template>
        <pdf-chart v-else title="Transfer reasons" subtitle="Percentage (%)">
          <line-chart
            :labels="streamReasonsTimeGroupDataset"
            :datasets="timeGroupedStreamReasonsDataset"
            :chart-options="
              merge(
                {
                  aspectRatio: getInitialAspectRatio(3.5, 1.2),
                  scales: { y: { title: { text: 'Percentage (%)' } } },
                  plugins: {
                    legend: { display: true, position: legendPosition },
                    title: { display: true, text: 'Transfer reasons' },
                  },
                },
                CHART_PRESET_OPTIONS.PERCENTAGE,
              )
            "
          />
        </pdf-chart>
      </sme-card>
    </template>
  </section>
</template>

<script setup>
import merge from 'lodash/merge';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import PdfChart from '@/components/PdfChart.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import DonutChart from '@/components/charts/DonutChart.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import State from '@/state/State';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import {
  TIME_SERIES_VALUES,
  createCountWithPercentageTooltip,
  getDatasetColor,
  getInitialAspectRatio,
} from '@/utils/chart';
import { toInteger, toNumber, currencySymbolFromCode } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();
const legendPosition = getInitialAspectRatio('right', 'bottom');

const loading = ref(true);
const timeDataset = ref(null);
const streamersDataset = ref(null);
const streamersPercentageDataset = ref(null);
const totalAmountDataset = ref(null);
const transfersDataset = ref(null);
const averageAmountDataset = ref(null);

const streamReasonsTimeDataset = ref(null);
const streamReasonsDatasets = ref(null);

const {
  timeGroupDataset,
  timeSeries,
  timeGroupTitle,
  timeSeriesUnit,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);
const {
  timeGroupDataset: streamReasonsTimeGroupDataset,
  datasetToTimeGroupedDataset: streamReasonsDatasetToTimeGroupedDataset,
} = useTimeGroupDataset(streamReasonsTimeDataset, timeSeries);

const timeGroupedStreamersDataset = computed(() => datasetToTimeGroupedDataset(streamersDataset));
const timeGroupedStreamerPercentageDataset = computed(() => datasetToTimeGroupedDataset(streamersPercentageDataset));
const timeGroupedStreamReasonsDataset = computed(() => streamReasonsDatasetToTimeGroupedDataset(streamReasonsDatasets));
const lastGroupedStreamers = computed(() => timeGroupedStreamersDataset.value?.calculated.last);
const prevGroupedStreamers = computed(() => timeGroupedStreamersDataset.value?.calculated.prev);
const lastGroupedStreamersPercentage = computed(() => timeGroupedStreamerPercentageDataset.value?.calculated.last);
const prevGroupedStreamersPercentage = computed(() => timeGroupedStreamerPercentageDataset.value?.calculated.prev);
const streamersPrevDiffText = computed(() => getPrevDiffText(timeGroupedStreamersDataset));
const bankingInfo = computed(() => getBankingInfo());

// Pie chart data for transfer reasons (feature-flagged)
const transferReasonsPieData = computed(() => {
  if (!streamReasonsDatasets.value?.length) {
    return [];
  }
  const items = streamReasonsDatasets.value
    .map(dataset => {
      const vals = dataset.values.map(v => Number(v)).filter(v => !isNaN(v));
      const lastVal = vals.length ? vals[vals.length - 1] : 0;
      return {
        label: dataset.name,
        value: lastVal,
        color: dataset.lineColor,
      };
    })
    .filter(item => item.value > 0);
  const total = items.reduce((s, i) => s + i.value, 0);
  if (!total) {
    return items;
  }
  return items.map(item => ({ ...item, value: Math.round((item.value / total) * 1000) / 10 }));
});
const transferReasonsLabels = computed(() =>
  transferReasonsPieData.value.map(i => i.label.replace(/\b\w/g, c => c.toUpperCase())),
);
const transferReasonsValues = computed(() => transferReasonsPieData.value.map(i => i.value));
const transferReasonsColors = computed(() => transferReasonsPieData.value.map(i => i.color));

watch([divisionId, timeSeries], () => getMetrics());

const getStreamMetrics = async () => {
  const { results } = await ApiClient.getStreamMetrics(timeSeries.value, divisionId.value);

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const streamers = results.find(result => result.id === 'streamers');
    const enrolled = results.find(result => result.id === 'enrolled');
    const totalAmounts = results.find(result => result.id === 'total_amount_streamed');
    const transfers = results.find(result => result.id === 'transfers');
    const averageAmountValues = totalAmounts.values.map((value, index) =>
      value ? value / transfers.values[index] : 0,
    );

    timeDataset.value = time;
    streamersDataset.value = {
      ...streamers,
      name: 'Number of Pay users',
    };
    streamersPercentageDataset.value = {
      name: 'Percentage (%)',
      values: enrolled.values.map((value, index) =>
        value ? Number(toNumber((streamers.values[index] / value) * 100)) : 0,
      ),
    };
    totalAmountDataset.value = {
      ...totalAmounts,
      name: 'Total',
      lineColor: PaletteColors['core-3'],
    };
    transfersDataset.value = {
      ...transfers,
      name: 'Transfers',
      lineColor: PaletteColors['core-4'],
    };
    averageAmountDataset.value = {
      name: 'Value',
      values: averageAmountValues,
      calculated: { mean: totalAmounts.calculated.mean / transfers.calculated.mean },
      lineColor: PaletteColors['core-5'],
    };
  }
};

const getStreamReasonsMetrics = async () => {
  const { results } = await ApiClient.getStreamReasonsMetrics(timeSeries.value, divisionId.value);

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const categories = results.filter(result => result.id !== 'time');

    streamReasonsTimeDataset.value = time;
    streamReasonsDatasets.value = categories.map((category, index) => ({
      ...category,
      lineColor: PaletteColors[getDatasetColor(index)],
      values: category.values.map(value => toNumber(value * 100)),
    }));
  }
};

const getMetrics = async () => {
  loading.value = true;

  await Promise.all([getStreamMetrics(), getStreamReasonsMetrics()]);

  loading.value = false;
};

onBeforeMount(() => getMetrics());

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values &&
    streamersDataset.value?.values &&
    streamersPercentageDataset.value?.values &&
    totalAmountDataset.value?.values &&
    transfersDataset.value?.values &&
    averageAmountDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    streamers: streamersDataset.value.values[index],
    percentage: streamersPercentageDataset.value.values[index],
    totalAmount: totalAmountDataset.value.values[index],
    transfers: transfersDataset.value.values[index],
    averageAmount: averageAmountDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return [
    'Date',
    streamersDataset.value.name,
    streamersPercentageDataset.value.name,
    'Total Amount',
    'Transfers',
    'Average Amount',
  ];
});

const exportFileRows = item => [
  item.time,
  item.streamers,
  item.percentage,
  Math.round(item.totalAmount),
  item.transfers,
  Number(item.averageAmount.toFixed(2)),
];

const hasData = computed(() => !loading.value && !!timeGroupedStreamersDataset.value?.values?.length);

defineExpose({ hasData });
</script>

<style lang="scss" scoped>
.transfer-reasons-title {
  font-weight: 700;
}

.transfer-reasons-chart {
  width: 220px;
  max-width: 100%;
}

.transfer-reasons-legend {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__label {
    flex: 1;
    font-size: 13px;
    color: var(--palette-color-default);
  }

  &__value {
    font-size: 13px;
    font-weight: 600;
    color: var(--palette-color-default);
  }
}

.stats-combined {
  display: flex;

  @media (max-width: 576px) {
    flex-direction: column;

    :deep(.app-stat) {
      &:first-child {
        border-radius: 4px 4px 0 0 !important;
        border-right: none !important;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      }

      &:last-child {
        border-radius: 0 0 4px 4px !important;
      }
    }
  }

  :deep(.app-stat) {
    flex: 1;
    height: auto;
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}
</style>
